import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal, Spinner } from "react-bootstrap";
import { AssessmentService } from "../../../../_services/company/assessmentService";
import { formLabel, primaryBtn, secondaryBtn } from "./assessmentStyles";

/**
 * Reusable create / edit modal for the company's KPI bank. Surfaces the KPI
 * name and a default weightage; every other field is defaulted server-side
 * (question_type is hard-coded to 'qualitative' on the request). Pass
 * `initialKpi` to edit. Emits the persisted row via `onSaved(kpi)`.
 */
const INPUT_TYPES = {
  QUANTITATIVE: "quantitative",
  YES_NO: "yes_no",
};

const YES_NO_DETAILS = [
  { option_type: "column", option: "Yes" },
  { option_type: "column", option: "No" },
];

const detectInputType = (kpi) => {
  if (!kpi) return INPUT_TYPES.QUANTITATIVE;
  if (kpi.question_type === "quantitative") return INPUT_TYPES.QUANTITATIVE;
  const opts = Array.isArray(kpi.details)
    ? kpi.details
        .filter((d) => d?.option_type === "column")
        .map((d) => String(d?.option || "").toLowerCase())
    : [];
  if (opts.length === 2 && opts.includes("yes") && opts.includes("no")) {
    return INPUT_TYPES.YES_NO;
  }
  return INPUT_TYPES.QUANTITATIVE;
};

const KpiCreateEditModal = ({ show, onClose, initialKpi = null, onSaved }) => {
  const isEdit = Boolean(initialKpi?.id);
  const [name, setName] = useState("");
  const [defaultWeightage, setDefaultWeightage] = useState("");
  const [inputType, setInputType] = useState(INPUT_TYPES.QUANTITATIVE);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!show) return;
    setName(initialKpi?.question_explanation || "");
    setDefaultWeightage(
      initialKpi?.default_weightage != null
        ? String(initialKpi.default_weightage)
        : ""
    );
    setInputType(detectInputType(initialKpi));
  }, [show, initialKpi]);

  const canSubmit = useMemo(
    () => name.trim().length > 0 && !saving,
    [name, saving]
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    const trimmedWeightage = defaultWeightage.trim();
    const isQuantitative = inputType === INPUT_TYPES.QUANTITATIVE;
    const payload = {
      question_explanation: name.trim(),
      question_type: isQuantitative ? "quantitative" : "qualitative",
      details: isQuantitative ? null : YES_NO_DETAILS,
      default_weightage:
        trimmedWeightage === "" ? null : Number(trimmedWeightage),
    };
    const res = isEdit
      ? await AssessmentService.updateCompanyKpi(initialKpi.id, payload)
      : await AssessmentService.createCompanyKpi(payload);
    setSaving(false);
    if (res?.isSuccess) {
      const saved = res?.data?.data || { ...payload, id: initialKpi?.id };
      onSaved?.(saved);
      onClose?.();
    }
  };

  return (
    <Modal show={show} onHide={onClose} size="md" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 16, fontWeight: 700 }}>
          {isEdit ? "Edit KPI" : "Create KPI"}
        </Modal.Title>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ background: "#fafbfc" }}>
          <label style={formLabel}>KPI name *</label>
          <Form.Control
            as="textarea"
            rows={3}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. How many tonnes of CO2 were emitted?"
            autoFocus
          />
          <label style={{ ...formLabel, marginTop: 12 }}>Answer type *</label>
          <div style={{ display: "flex", gap: 16, marginTop: 4 }}>
            <label style={{ display: "flex", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="kpi-input-type"
                value={INPUT_TYPES.QUANTITATIVE}
                checked={inputType === INPUT_TYPES.QUANTITATIVE}
                onChange={() => setInputType(INPUT_TYPES.QUANTITATIVE)}
              />
              <span style={{ fontSize: 13 }}>Quantitative (number)</span>
            </label>
            <label style={{ display: "flex", gap: 6, cursor: "pointer" }}>
              <input
                type="radio"
                name="kpi-input-type"
                value={INPUT_TYPES.YES_NO}
                checked={inputType === INPUT_TYPES.YES_NO}
                onChange={() => setInputType(INPUT_TYPES.YES_NO)}
              />
              <span style={{ fontSize: 13 }}>Yes / No</span>
            </label>
          </div>
          <label style={{ ...formLabel, marginTop: 12 }}>
            Default weightage
          </label>
          <Form.Control
            type="number"
            min="0"
            step="0.01"
            value={defaultWeightage}
            onChange={(e) => setDefaultWeightage(e.target.value)}
            placeholder="Leave blank for no default"
          />
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>
            Each assessment can override this per KPI. Blank = no default.
          </div>
        </Modal.Body>
        <Modal.Footer style={{ background: "var(--surface)", gap: 8 }}>
          <button type="button" style={secondaryBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="submit"
            style={{ ...primaryBtn, opacity: canSubmit ? 1 : 0.6 }}
            disabled={!canSubmit}
          >
            {saving ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  style={{ marginRight: 8 }}
                />
                Saving…
              </>
            ) : isEdit ? (
              "Save changes"
            ) : (
              "Create KPI"
            )}
          </button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default KpiCreateEditModal;
